import { AlertTriangle, Gem, Inbox, Loader2 } from "lucide-react";

export function Toggle({ checked, onChange, testid, disabled, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} data-testid={testid} disabled={disabled} onClick={() => onChange(!checked)}
      className={`w-11 h-6 rounded-full transition-colors relative shrink-0 disabled:opacity-50 ${checked ? "bg-volt" : "bg-zinc-700"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-[#fff] shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

export function StatCard({ icon: Icon, label, value, accent = "#0EA5E9" }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">{label}</span>
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}1f` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </span>
      </div>
      <div className="font-head text-2xl sm:text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

export function PageHead({ title, desc, children }) {
  return (
    <div className="flex items-start justify-between mb-6 sm:mb-8 flex-wrap gap-4">
      <div className="min-w-0">
        <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {desc && <p className="text-zinc-400 mt-1 text-sm max-w-2xl">{desc}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Panel({ title, desc, actions, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`bg-zinc-900 border border-zinc-800 rounded-xl ${className}`}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 pt-5">
          <div><h3 className="font-head font-semibold">{title}</h3>{desc && <p className="text-sm text-zinc-500 mt-0.5">{desc}</p>}</div>
          {actions}
        </div>
      )}
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}

export function LoadingBlock({ label = "Loading…" }) {
  return (
    <div data-testid="loading-state" className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> {label}</div>
      {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-zinc-900 border border-zinc-800 animate-pulse" />)}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, desc, children }) {
  return (
    <div data-testid="empty-state" className="text-center py-12 px-6 border border-dashed border-zinc-800 rounded-xl">
      <Icon className="w-8 h-8 mx-auto text-zinc-600 mb-3" />
      <div className="font-head font-semibold">{title}</div>
      {desc && <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">{desc}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", desc, onRetry }) {
  return (
    <div data-testid="error-state" className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
      <div className="flex-1"><div className="font-semibold text-red-400">{title}</div>{desc && <p className="text-sm text-zinc-400 mt-0.5">{desc}</p>}</div>
      {onRetry && <button onClick={onRetry} data-testid="error-retry" className="text-sm border border-zinc-700 rounded-lg px-3 py-1.5 hover:bg-zinc-800">Retry</button>}
    </div>
  );
}

export function PremiumBanner({ guildId, tier = "Premium", locked }) {
  if (!locked) return null;
  return (
    <div data-testid="premium-banner" className="mb-6 rounded-xl border border-volt/30 bg-volt/5 p-4 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-volt/15 flex items-center justify-center"><Gem className="w-5 h-5 text-volt" /></span>
        <div>
          <div className="font-head font-semibold text-volt">{tier} feature</div>
          <div className="text-sm text-zinc-400">This module is locked on the Free plan. Upgrade to unlock it.</div>
        </div>
      </div>
      <a href={`/app/g/${guildId}/billing`} className="bg-volt text-volt-ink font-semibold rounded-lg px-5 py-2 hover:bg-volt-hover transition-colors">Upgrade</a>
    </div>
  );
}

export function PremiumBadge() {
  return <span className="text-[9px] font-bold uppercase tracking-wider bg-volt/15 text-volt px-1.5 py-0.5 rounded ml-auto">PRO</span>;
}
